import {fieldByKey, ReportTaxonomy} from '../contracts/report-taxonomy.types';

/** Human-readable labels for the 12 default field paths, plus `deliverable`, which is available
 * but not default. Consumers may override/extend via `useReportTableColumns`'s `fieldLabels`
 * option. */
export const DEFAULT_FIELD_LABELS: Record<string, string> = {
	'task.project.info': 'Project',
	'task.phase.name': 'Phase',
	'task.status.name': 'Status',
	'task.companyTaskId': 'Task ID',
	'task.name': 'Task',
	'task.assignees.firstName': 'Assignee',
	'task.work.estimate': 'Estimate',
	'task.reported.time': 'Reported time',
	'task.work.remaining': 'Remaining',
	'task.dates.start': 'Start date',
	'task.dates.deadline': 'Deadline',
	'task.role.info': 'Role',
	'task.deliverable.name': 'Deliverable',
};

/**
 * The display name for a field path, from the first source that has one:
 *
 * 1. an explicit override the caller passed,
 * 2. the static map above,
 * 3. the taxonomy's own label for that field key,
 * 4. the raw path, which at least identifies the column.
 *
 * The static map is consulted BEFORE the taxonomy on purpose, and the order is load-bearing rather
 * than arbitrary. Three of the thirteen entries deliberately differ from the catalog's wording -
 * the table says Task, Assignee and Remaining where the catalog says Task name, Assignee first name
 * and Remaining work - because a column heading is tighter than a field's full name. Reading the
 * taxonomy first would silently rename three live columns on every task report.
 *
 * A grain with no static entries - which is every grain but task - therefore gets its headings from
 * the taxonomy, which is where they have always been published and where the conditions editor and
 * the column picker already read them from.
 */
export function labelForField(path: string, taxonomy?: ReportTaxonomy | null, overrides?: Record<string, string>): string {
	return (
		overrides?.[path] ?? DEFAULT_FIELD_LABELS[path] ?? (taxonomy ? fieldByKey(taxonomy, path)?.label : undefined) ?? path
	);
}
